import { useDropzone } from 'react-dropzone'
import { useCallback, useRef, useState } from 'react'
import { Button, cn } from '@wove/web-ui'
import axios from 'axios'
import {
  SourceStatus,
  SourceUploadProgress,
  GetPresignedUrlForSourceUploadResponse,
} from '@wove/api/types'
import {
  useGetPresignedUrlMutation,
  useGetSourcesQuery,
  useStartProcessingSourceMutation,
} from '@wove/api'
import useUploadPolling from '@src/Pages/UploadPage/useUploadPolling'
import UploadStatusOverlay from '@src/Pages/UploadPage/UploadStatusOverlay'
import UpdateFillInfoModal from '@src/Pages/UploadPage/UploadFillInfoModal'
import { getSourceUploadProgressQuery } from '@wove/api'
import { useClient } from 'urql'
import { FileUp, Upload } from 'lucide-react'

interface FileUploadSectionProps {
  className?: string
}

const FileUploadSection = ({ className }: FileUploadSectionProps) => {
  const [isDropAnimation, setIsDropAnimation] = useState(false)
  const [{ fetching: executingUploadMutation }, executeUpload] = useGetPresignedUrlMutation()
  const [{ fetching: executingProcessingMutation }, executeProcessingMutation] =
    useStartProcessingSourceMutation()
  const [queryData, refetchSources] = useGetSourcesQuery()
  const [selectedFile, setSelectedFile] = useState<null | File>(null)
  const [appendingFile, setAppendingFile] = useState(false)
  const client = useClient()
  const {
    sourceStatuses,
    pollTimeouts,
    removeSourceStatus,
    addPollingTimeout,
    removePollingTimeout,
    setSourceStatus,
    addCompletedSource,
    completedSources,
  } = useUploadPolling()
  const shouldBeginPolling = useRef(false)

  const pollSourceStatus = useCallback(
    async (status: SourceStatus, id: string, fileName: string) => {
      const pollTimeout = pollTimeouts.current[id]
      if (status === SourceStatus.Complete) {
        //Complete
        if (pollTimeout) {
          removePollingTimeout(id)
          addCompletedSource(id)
        }
        return
      }
      if (status === SourceStatus.Failed) {
        //Failed
        if (pollTimeout) {
          removePollingTimeout(id)
        }
        return
      }

      const result = await client
        .query(
          getSourceUploadProgressQuery,
          {
            getSourceUploadProgressId: id,
          },
          {
            requestPolicy: 'network-only',
          },
        )
        .toPromise()

      if (result.data) {
        const progressStatus = result.data.getSourceUploadProgress
        setSourceStatus(id, {
          fileName,
          progress: progressStatus,
        })
        if (progressStatus.status === SourceStatus.Complete) {
          removePollingTimeout(id)
          addCompletedSource(id)
          refetchSources({ requestPolicy: 'network-only' })
        } else if (progressStatus.status === SourceStatus.Failed) {
          removePollingTimeout(id)
        } else {
          addPollingTimeout(
            id,
            setTimeout(() => {
              pollSourceStatus(progressStatus.status, id, fileName)
            }, 1000),
          )
        }
      }
    },
    [
      pollTimeouts,
      removePollingTimeout,
      addCompletedSource,
      client,
      setSourceStatus,
      addPollingTimeout,
      refetchSources,
    ],
  )

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      setSelectedFile(file)
      setIsDropAnimation(true)

      setTimeout(() => {
        setIsDropAnimation(false)
      }, 800)
    }
  }

  const onBeginProcessing = useCallback(
    async ({
      file,
      tagIds,
      // description,
      effectiveDate,
      expiryDate,
      carrier,
    }: {
      file: File
      tagIds: string[]
      effectiveDate?: Date
      expiryDate?: Date
      carrier?: string | null
    }) => {
      try {
        shouldBeginPolling.current = true
        const result = await executeUpload({ name: file.name, tagIds })
        if (result.error) {
          throw result.error
        } else {
          const res = result.data
            ?.getPresignedUrlForSourceUpload as GetPresignedUrlForSourceUploadResponse
          await axios.put(res.url, file, {
            headers: {
              'Content-Type': file.type,
            },
          })
          setAppendingFile(false)
          const processingResult = await executeProcessingMutation({
            startProcessingSourceId: res.id,
            options: {
              carrierCode: carrier,
              effectiveDate: effectiveDate?.toISOString(),
              expiryDate: expiryDate?.toISOString(),
            },
          })
          if (shouldBeginPolling.current && processingResult.data) {
            setSelectedFile(null)
            shouldBeginPolling.current = false
            setSourceStatus(res.id, {
              fileName: file.name,
              progress: processingResult.data.startProcessingSource as SourceUploadProgress,
            })
            pollSourceStatus(processingResult.data.startProcessingSource.status, res.id, file.name)
          }
        }
      } catch (error) {
        setAppendingFile(false)
      }
    },
    [executeUpload, executeProcessingMutation, setSourceStatus, pollSourceStatus],
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      // pdf
      'application/pdf': ['.pdf'],
    },
  })

  return (
    <>
      <div className={cn('flex flex-col gap-6', className)}>
        <div className="flex flex-col gap-2">
          <div
            className={cn(
              'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 text-center transition-all duration-300',
              isDragActive
                ? 'border-primary bg-primary/5 scale-[1.02] shadow-lg'
                : 'border-muted-foreground/25',
              isDropAnimation && 'animate-pulse border-primary bg-primary/5',
            )}
            {...getRootProps()}
          >
            <div
              className={cn(
                'flex flex-col items-center justify-center space-y-4 transition-transform duration-300',
                isDropAnimation && 'scale-110',
              )}
            >
              <div
                className={cn(
                  'rounded-full bg-primary/10 p-4 transition-all duration-300',
                  isDropAnimation && 'bg-primary/30',
                )}
              >
                <FileUp
                  className={cn(
                    'h-8 w-8 text-primary transition-all duration-300',
                    isDropAnimation && 'text-primary/80',
                  )}
                />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Drop your files here</h3>
                <p className="text-sm text-muted-foreground">
                  or click to browse files from your computer
                </p>
                <p className="text-xs text-muted-foreground">
                  Supports: CSV, XLSX, XLS, PDF or TXT
                </p>
              </div>
              <Button variant="outline" className="transition-all duration-200 hover:scale-105">
                <Upload className="mr-2 h-4 w-4" />
                Browse Files
              </Button>
              <input {...getInputProps()} />
            </div>
          </div>
          <UploadStatusOverlay
            sourceStatuses={sourceStatuses}
            completedSources={completedSources}
            sources={queryData.data?.getSources}
            onRemove={(id) => {
              removePollingTimeout(id)
              removeSourceStatus(id)
            }}
          />
          <UpdateFillInfoModal
            isOpen={!!selectedFile}
            file={selectedFile}
            onClose={() => setSelectedFile(null)}
            onBeginUpload={({ tags, effectiveDate, expiryDate, carrier }) =>
              selectedFile &&
              onBeginProcessing({
                file: selectedFile,
                tagIds: tags.map((tag) => tag.id),
                effectiveDate: effectiveDate || undefined,
                expiryDate: expiryDate || undefined,
                carrier: carrier?.code || null,
              })
            }
            uploading={executingUploadMutation || executingProcessingMutation || appendingFile}
          />
        </div>
      </div>
    </>
  )
}

export default FileUploadSection
